import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Search, ArrowLeft, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOMAIN_LABELS = {
  constitution: 'Constitution',
  famille: 'Code de la Famille',
  penal: 'Code Penal',
  travail: 'Code du Travail',
  investissement: 'Code des Investissements',
  numerique: 'Code du Numerique',
  procedure_penale: 'Code de Procedure Penale',
  protection_enfant: "Loi sur la Protection de l'Enfant",
  foncier: 'Code Foncier et Immobilier',
  entrepreneuriat: "Loi sur l'Entrepreneuriat et les Startups",
  baux_loyers: 'Baux à Loyers',
  obligations: 'Code des Contrats et Obligations',
  barreau: 'Organisation du Barreau',
  minier: 'Code Minier',
  procedure_civile: 'Code de Procédure Civile',
};

const FILTERS = [
  { code: '', name: 'Tous' },
  { code: 'constitution', name: 'Constitution' },
  { code: 'famille', name: 'Famille' },
  { code: 'obligations', name: 'Contrats & Obligations' },
  { code: 'penal', name: 'Penal' },
  { code: 'travail', name: 'Travail' },
  { code: 'investissement', name: 'Investissement' },
  { code: 'numerique', name: 'Numerique' },
  { code: 'procedure_penale', name: 'Procedure Penale' },
  { code: 'protection_enfant', name: "Protection de l'Enfant" },
  { code: 'foncier', name: 'Foncier' },
  { code: 'entrepreneuriat', name: 'Entrepreneuriat' },
  { code: 'baux_loyers', name: 'Baux à Loyers' },
];

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [domain, setDomain] = useState('');
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [labels, setLabels] = useState(DOMAIN_LABELS);

  // Libellés dynamiques (inclut OHADA et toute loi ajoutée)
  useEffect(() => {
    fetch(`${API_URL}/api/domains`)
      .then(r => r.json())
      .then(data => {
        if (!Array.isArray(data)) return;
        const map = { ...DOMAIN_LABELS };
        data.forEach(d => { map[d.code] = d.name; });
        setLabels(map);
      })
      .catch(() => {});
  }, []);

  // Autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);
  const suggestAbortRef = useRef(null);
  const suggestTimerRef = useRef(null);
  const inputRef = useRef(null);
  const lastSubmittedRef = useRef('');

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setQuery(q);
      lastSubmittedRef.current = q.trim();
      doSearch(q, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Fetch autocomplete suggestions with debounce
  useEffect(() => {
    if (suggestTimerRef.current) clearTimeout(suggestTimerRef.current);
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    // Pas de suggestions si l'input n'a pas le focus, ou si la query est
    // celle qui vient juste d'etre soumise (Enter / chargement initial via ?q=).
    if (!isFocused || trimmed === lastSubmittedRef.current) {
      return;
    }
    suggestTimerRef.current = setTimeout(async () => {
      if (suggestAbortRef.current) suggestAbortRef.current.abort();
      const ctrl = new AbortController();
      suggestAbortRef.current = ctrl;
      try {
        const params = new URLSearchParams({ q: trimmed, limit: '6' });
        if (domain) params.set('domain', domain);
        const res = await fetch(`${API_URL}/api/search/suggest?${params}`, {
          signal: ctrl.signal,
        });
        const data = await res.json();
        setSuggestions(data.suggestions || []);
        // On ne montre que si l'input a TOUJOURS le focus a la reception
        if (document.activeElement === inputRef.current) {
          setShowSuggestions(true);
          setActiveIndex(-1);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          setSuggestions([]);
        }
      }
    }, 220);
    return () => {
      if (suggestTimerRef.current) clearTimeout(suggestTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, domain, isFocused]);

  const doSearch = async (q, dom) => {
    if (!q.trim()) return;
    lastSubmittedRef.current = q.trim();
    setShowSuggestions(false);
    setSuggestions([]);
    if (inputRef.current) inputRef.current.blur();
    setLoading(true);
    setSearched(true);
    try {
      const body = { query: q, limit: 30 };
      if (dom) body.domain = dom;
      const res = await fetch(`${API_URL}/api/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      setResults(data.results || []);
      setTotal(data.total || 0);
    } catch {
      setResults([]);
    }
    setLoading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    doSearch(query, domain);
  };

  const handleFilterChange = (code) => {
    setDomain(code);
    if (query.trim()) doSearch(query, code);
  };

  const handleSuggestionClick = (s) => {
    setShowSuggestions(false);
    navigate(`/article/${s.id}`);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSuggestionClick(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <div>
      <div style={{ padding: '24px 20px 0' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 800, color: 'var(--text-dark)', marginBottom: '16px' }}
            data-testid="search-title">
          Recherche
        </h1>
        <form onSubmit={handleSubmit}>
          <div className="search-wrapper" style={{ position: 'relative' }}>
            <div className="search-bar" data-testid="search-input">
              <Search />
              <input
                ref={inputRef}
                type="text"
                placeholder="Rechercher une loi, un article..."
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  // L'utilisateur tape : la query n'est plus celle du dernier submit
                  lastSubmittedRef.current = '';
                }}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                  setIsFocused(true);
                  if (suggestions.length > 0 && query.trim().length >= 2) {
                    setShowSuggestions(true);
                  }
                }}
                onBlur={() => {
                  setIsFocused(false);
                  // Petit delai pour permettre au mouseDown sur une suggestion de gagner
                  setTimeout(() => setShowSuggestions(false), 150);
                }}
                autoComplete="off"
              />
            </div>

            {showSuggestions && suggestions.length > 0 && (
              <ul
                className="search-suggestions"
                role="listbox"
                data-testid="search-suggestions"
              >
                {suggestions.map((s, i) => (
                  <li
                    key={s.id}
                    role="option"
                    aria-selected={i === activeIndex}
                    className={`suggestion-item ${i === activeIndex ? 'active' : ''}`}
                    onMouseDown={(e) => { e.preventDefault(); handleSuggestionClick(s); }}
                    data-testid={`suggestion-${s.id}`}
                  >
                    <FileText className="suggestion-icon" />
                    <div className="suggestion-text">
                      <div className="suggestion-title">
                        {s.article_number}
                        <span className="suggestion-domain">{s.domain_label}</span>
                      </div>
                      <div className="suggestion-snippet">{s.snippet}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </form>

        <div className="search-filters" data-testid="search-filters">
          {FILTERS.map(f => (
            <button
              key={f.code}
              className={`filter-chip ${domain === f.code ? 'active' : ''}`}
              onClick={() => handleFilterChange(f.code)}
              data-testid={`filter-${f.code || 'all'}`}
            >
              {f.name}
            </button>
          ))}
        </div>
      </div>

      <div className="page-container">
        {loading ? (
          <div className="loading-spinner" />
        ) : searched && results.length === 0 ? (
          <div className="empty-state">
            <h2>Aucun resultat</h2>
            <p>Essayez avec d'autres termes de recherche.</p>
          </div>
        ) : (
          <>
            {total > 0 && (
              <p style={{ fontSize: '13px', color: 'var(--text-gray)', marginBottom: '12px' }}>
                {total} resultat{total > 1 ? 's' : ''} trouve{total > 1 ? 's' : ''}
              </p>
            )}
            {results.map(r => (
              <Link
                key={r.id}
                to={`/article/${r.id}`}
                className="search-result-card"
                data-testid={`result-${r.id}`}
              >
                <div className="result-domain">{labels[r.domain] || r.domain}</div>
                <div className="result-number">{r.article_number}</div>
                <div className="result-summary">{r.summary?.substring(0, 120)}...</div>
              </Link>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
